import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { Avatar } from '../../avatar';
import { Icon } from '../../icon';
import { InputCheckbox } from '../input_checkbox';
import { useInputComboboxContext } from './_input_combobox.context';
import { theme } from './input_combobox.theme';
import { InputComboboxOption } from './input_combobox_root.component';

interface InputComboboxRenderItemProps {
	option: InputComboboxOption;
}

export function InputComboboxRenderItem({ option }: InputComboboxRenderItemProps) {
	const { onSelectOption, multiple, value, readOnly } = useInputComboboxContext();
	const isSelected = value.includes(option.value);

	function handleOnSelect() {
		onSelectOption(option);
	}

	const renderLeading = () => {
		if (option.avatar) {
			return (
				<elements.avatar>
					<Avatar size='small' src={option.avatar.src} name={option.avatar.name ?? option.label} />
					{option.avatar.status ? <elements.statusDot data-status={option.avatar.status} /> : null}
				</elements.avatar>
			);
		}
		if (option.icon) {
			return (
				<elements.leadingIcon>
					<Icon type={option.icon} />
				</elements.leadingIcon>
			);
		}
		return null;
	};

	if (multiple) {
		return (
			<elements.itemCheckbox
				label={option.label}
				subLabel={option.subLabel}
				checked={isSelected}
				readOnly={readOnly}
				disabled={option.disabled}
				onChange={handleOnSelect}
			/>
		);
	}

	return (
		<elements.itemButton
			type='button'
			onClick={handleOnSelect}
			aria-selected={isSelected}
			aria-readonly={readOnly}
			aria-disabled={option.disabled}
			disabled={readOnly || option.disabled}
		>
			<elements.content>
				{renderLeading()}
				{/** biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed */}
				<elements.label>
					{option.label}
					{option.subLabel ? <elements.subLabel>{option.subLabel}</elements.subLabel> : null}
				</elements.label>
				{isSelected ? (
					<elements.icon>
						<Icon type='check' />
					</elements.icon>
				) : null}
			</elements.content>
		</elements.itemButton>
	);
}

const elements = {
	itemCheckbox: styled(InputCheckbox)`
		display: flex;
		padding: ${theme.getValue('itemPadding')};
		min-height: ${theme.getValue('itemMinHeight')};
		flex: 1 0 auto;
		font: ${theme.getValue('itemFont')};
		color: ${theme.getValue('itemFontColor')};

		&[data-state='checked'] {
			font: ${theme.getValue('itemSelectedFont')};
			background: ${theme.getValue('itemSelectedBackground')};
		}

		&:focus-visible {
			outline: 2px solid ${theme.getValue('itemFocusColor')};
			outline-offset: 0;
		}

		@media (hover: hover) {
			&:hover:not([aria-readonly='true']) {
				background: ${theme.getValue('itemHoverBackground')};
			}
		}
	`,
	itemButton: styled.button`
		all: unset;
		box-sizing: border-box;
		font: ${theme.getValue('itemFont')};
		color: ${theme.getValue('itemFontColor')};
		border-radius: ${theme.getValue('itemBorderRadius')};

		width: 100%;
		padding: ${theme.getValue('itemPadding')};
		position: relative;
		user-select: none;
		transition: background 150ms;
		word-break: break-word;

		&[aria-selected='true'] {
			font: ${theme.getValue('itemSelectedFont')};
			background: ${theme.getValue('itemSelectedBackground')};
		}

		&:focus-visible {
			outline: 2px solid ${theme.getValue('itemFocusColor')};
		}

		&[aria-readonly='true'],
		&[aria-disabled='true'] {
			opacity: 0.7;
		}

		&[aria-disabled='true'] {
			cursor: not-allowed;
		}

		@media (hover: hover) {
			&:hover:not([aria-readonly='true']):not([aria-disabled='true']) {
				background: ${theme.getValue('itemHoverBackground')};
			}
		}
	`,
	content: styled.span`
		display: flex;
		align-items: center;
		gap: ${coreTokens.spacing.x1_5};
		min-height: ${theme.getValue('itemMinHeight')};
	`,
	label: styled.span`
		display: flex;
		flex-direction: column;
		gap: 0;
	`,
	subLabel: styled.span`
		font: ${theme.getValue('itemSubLabelFont')};
		color: ${theme.getValue('itemSubLabelFontColor')};
	`,
	icon: styled.span`
		flex: 0 0 auto;
		margin-left: auto;
	`,
	leadingIcon: styled.span`
		flex: 0 0 auto;
		display: inline-flex;
		align-items: center;
	`,
	avatar: styled.span`
		position: relative;
		flex: 0 0 auto;
		display: inline-flex;
	`,
	statusDot: styled.span`
		position: absolute;
		bottom: 0;
		right: 0;
		width: 12px;
		height: 12px;
		border-radius: 100%;
		border: 2px solid ${theme.getValue('itemStatusRingColor')};

		&[data-status='online'] {
			background-color: ${theme.getValue('itemStatusOnlineColor')};
		}

		&[data-status='offline'] {
			background-color: ${theme.getValue('itemStatusOfflineColor')};
		}
	`,
};
